import { log, LogLevel } from "utils/LogUtil";
import {
  SubscriptionInitDto,
  SubscriptionBillingCycle,
  User,
} from "common/models/types";
import type { IMPResponse } from "types/iamport";
import "types/iamport"; // 글로벌 Window.IMP 선언 활성화
import { DateTime } from "luxon";

// 기존 포인트 결제와 동일한 가맹점 식별코드
// pages/MyPage/Points/index.tsx의 imp68909306 참조
const IMP_MERCHANT_CODE = process.env.REACT_APP_IMP_MERCHANT_CODE || "imp68909306";

// 운영팀이 결정한 PG 채널. 기존 포인트 결제와 동일하게 danal_tpay 사용.
// local/dev에서는 .env로 테스트 채널(danal_tpay.{상점MID})을 주입.
// 미설정 시(운영) 기존값 danal_tpay 사용.
const PG = process.env.REACT_APP_IMP_PG || "danal_tpay";

export type SubscriptionAction = "register" | "change";

/**
 * 정기결제 period 계산 (KST 기준 YYYYMMDD).
 * - monthly: 오늘 ~ (한 달 후 -1일)
 * - yearly: 오늘 ~ (1년 후 -1일)
 * ymyd-frontend의 getDateStringFromToday({ months: 1, days: -1, separator: "" }) 패턴.
 */
export const computePeriod = (
  billingCycle: SubscriptionBillingCycle,
  now: DateTime = DateTime.now()
): { from: string; to: string } => {
  // 테스트 가능하도록 now 주입 가능 (기본은 현재 시각)
  const _now = now.setZone("Asia/Seoul");
  const end =
    billingCycle === "yearly"
      ? _now.plus({ years: 1 }).minus({ days: 1 })
      : _now.plus({ months: 1 }).minus({ days: 1 });
  return {
    from: _now.toFormat("yyyyMMdd"),
    to: end.toFormat("yyyyMMdd"),
  };
};

export interface SubscriptionPayResult {
  success: boolean;
  impUid?: string;
  merchantUid?: string;
  errorMsg?: string;
  errorCode?: string;
}

const ensureImpReady = (): boolean => {
  if (!window.IMP) {
    log(LogLevel.ERROR, "[subscriptionPayment] IMP 객체 미로드");
    return false;
  }
  try {
    window.IMP.init(IMP_MERCHANT_CODE);
    return true;
  } catch (error) {
    log(LogLevel.ERROR, "[subscriptionPayment] IMP 초기화 실패:", error);
    return false;
  }
};

/**
 * 구독 가입 / 카드 변경 공통 호출.
 *
 * customer_uid / merchant_uid / amount / productName 은 백엔드가 발급한
 * SubscriptionInitDto의 값을 그대로 PortOne SDK에 전달한다.
 */
export const requestSubscriptionPay = (
  init: SubscriptionInitDto,
  action: SubscriptionAction,
  buyer: Pick<User, "email" | "name" | "phone">
): Promise<SubscriptionPayResult> =>
  new Promise((resolve) => {
    if (!ensureImpReady()) {
      resolve({ success: false, errorMsg: "결제 모듈이 로드되지 않았습니다" });
      return;
    }

    // 결제창 표시명에 "정기" 명시. 백엔드 productName이 이미 "PhAI 유료 프리미엄 (월)" 형태이지만
    // 사용자에게 정기결제임을 더 분명히 알리기 위해 prefix 추가.
    const displayName = init.productName.includes("정기")
      ? init.productName
      : `${init.productName} 정기결제`;

    // 가입(register)에는 period 명시 → PortOne 결제창에서 정기결제로 표시.
    // 카드 변경(change)에는 period 미포함 (ymyd-frontend 패턴 — 금액 0 검증 결제).
    const period =
      action === "register"
        ? computePeriod(init.billingCycle, DateTime.now())
        : undefined;

    const params: Record<string, unknown> = {
      pg: PG,
      pay_method: "card",
      customer_uid: init.customerUid,
      merchant_uid: init.merchantUid,
      // 카드 변경은 빌링키 재발급용 0원 검증 결제 — 실제 청구 금지.
      // (register만 init.amount로 첫 정기결제 청구)
      amount: action === "change" ? 0 : init.amount,
      name: displayName,
      buyer_email: buyer.email,
      buyer_name: buyer.name,
      buyer_tel: buyer.phone,
      custom_data: {
        subscriptionId: init.subscriptionId,
        action,
      },
    };
    if (period) {
      params.period = period;
    }

    log(LogLevel.INFO, "[subscriptionPayment] request_pay 호출", {
      action,
      subscriptionId: init.subscriptionId,
      merchantUid: init.merchantUid,
      period,
    });

    window.IMP.request_pay(params, (rsp: IMPResponse) => {
      if (rsp.success) {
        log(LogLevel.INFO, "[subscriptionPayment] 결제 성공", {
          subscriptionId: init.subscriptionId,
          impUid: rsp.imp_uid,
          merchantUid: rsp.merchant_uid,
          action,
        });
        resolve({
          success: true,
          impUid: rsp.imp_uid,
          merchantUid: rsp.merchant_uid,
        });
      } else {
        log(LogLevel.ERROR, "[subscriptionPayment] 결제 실패", {
          subscriptionId: init.subscriptionId,
          merchantUid: init.merchantUid,
          action,
          errorCode: rsp.error_code,
          errorMsg: rsp.error_msg,
          impUid: rsp.imp_uid,
        });
        resolve({
          success: false,
          impUid: rsp.imp_uid,
          merchantUid: rsp.merchant_uid,
          errorMsg: rsp.error_msg,
          errorCode: rsp.error_code,
        });
      }
    });
  });
